import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ChevronUp, X, Trash2, Check, Send, Mail, CheckCircle2, FileText, Building2, ArrowLeftRight, Package, Lock, ArrowLeft } from 'lucide-react';
import { useSelections, Selection } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
type FinalizeStep = 'review' | 'email' | 'otp' | 'processing' | 'success';
interface SelectionsWidgetProps {
  onSwap?: (category: string, itemName: string) => void;
  isGalleryOpen?: boolean;
}
export function SelectionsWidget({
  onSwap,
  isGalleryOpen = false
}: SelectionsWidgetProps) {
  const {
    selections,
    totalPrice,
    recentlyAdded,
    clearRecentlyAdded,
    removeSelection,
    clearSelections,
    activePackage,
    clearActivePackage,
    isItemOverridingPackage,
    getPackageItemPrice,
    packageOverrides,
    resetPackageOverrides,
    getOriginalPackageOption
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [finalizeStep, setFinalizeStep] = useState<FinalizeStep>('review');
  const [confirmClear, setConfirmClear] = useState(false);
  // Auth mode: 'authenticated' skips email/OTP, 'guest' requires it
  const [authMode, setAuthMode] = useState<'authenticated' | 'guest'>('authenticated');
  // Email & OTP State
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(''));
  const [otpError, setOtpError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Show toast when new item is added
  useEffect(() => {
    if (recentlyAdded) {
      setShowToast(true);
      const timer = setTimeout(() => {
        setShowToast(false);
        clearRecentlyAdded();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded, clearRecentlyAdded]);
  const handleFinalize = () => {
    if (authMode === 'authenticated') {
      // Known user — skip email/OTP, go straight to processing
      setFinalizeStep('processing');
      setTimeout(() => setFinalizeStep('success'), 2500);
    } else {
      // Guest — need email + OTP
      setFinalizeStep('email');
    }
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && email.includes('@')) {
      setFinalizeStep('otp');
      // Auto-focus first OTP input after short delay for transition
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pasted = value.slice(0, 6).split('');
      const newDigits = [...otpDigits];
      pasted.forEach((char, i) => {
        if (index + i < 6) newDigits[index + i] = char;
      });
      setOtpDigits(newDigits);
      setOtpError(false);
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + pasted.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    setOtpError(false);
    // Auto-advance
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otpDigits.some(d => !d)) {
      setOtpError(true);
      return;
    }
    setIsVerifying(true);
    // Simulate verification
    setTimeout(() => {
      setIsVerifying(false);
      setFinalizeStep('processing');
      // Simulate processing
      setTimeout(() => {
        setFinalizeStep('success');
      }, 2500);
    }, 1000);
  };
  const handleClose = () => {
    setIsExpanded(false);
    // Reset to review step after closing
    setTimeout(() => {
      setFinalizeStep('review');
      setEmail('');
      setOtpDigits(Array(6).fill(''));
      setOtpError(false);
      setConfirmClear(false);
    }, 300);
  };
  const handleStartOver = () => {
    clearSelections();
    setFinalizeStep('review');
    setEmail('');
    setOtpDigits(Array(6).fill(''));
    setOtpError(false);
    setConfirmClear(false);
    setIsExpanded(false);
  };
  const handleSwap = (category: string, itemName: string) => {
    setIsExpanded(false);
    onSwap?.(category, itemName);
  };
  const itemCount = selections.length;
  if (itemCount === 0 && !activePackage && !showToast && finalizeStep === 'review') {
    return null;
  }
  return <>
      {/* Toast Notification */}
      <AnimatePresence data-id="element-1905">
        {showToast && recentlyAdded && <motion.div initial={{
        opacity: 0,
        y: 20,
        x: 20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed bottom-[140px] right-6 z-[100] pointer-events-none" data-id="element-1906">
            <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border bg-slate-900/80 border-white/10 text-white backdrop-blur-xl" data-id="element-1907">
              <div className="p-1.5 rounded-full bg-emerald-500" data-id="element-1908">
                <Check className="w-4 h-4 text-white" data-id="element-1909" />
              </div>
              <div className="flex flex-col" data-id="element-1910">
                <span className="text-sm font-semibold text-white" data-id="element-1911">
                  Added to selections
                </span>
                <span className="text-xs text-white/60" data-id="element-1912">
                  {recentlyAdded.itemName} - {recentlyAdded.optionName}
                </span>
              </div>
              <span className="text-sm font-bold ml-2 text-emerald-400" data-id="element-1913">
                {recentlyAdded.price === 0 ? 'Included' : `+${recentlyAdded.price.toLocaleString()}`}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Collapsed Widget */}
      <AnimatePresence data-id="element-1914">
        {!isExpanded && itemCount > 0 && <motion.button initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.8,
        opacity: 0
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={() => setIsExpanded(true)} className={`fixed right-6 z-[90] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl backdrop-blur-xl border transition-all duration-300 ${isDark ? 'bg-slate-900/80 border-cyan-500/30 hover:border-cyan-500/50' : 'bg-white/80 border-teal-500/30 hover:border-teal-500/50'} ${isGalleryOpen ? 'bottom-[360px]' : 'bottom-[80px]'}`} data-id="element-1915">
            <div className={`relative p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1916">
              <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1917" />
              <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-1918">
                {itemCount}
              </div>
            </div>
            <div className="flex flex-col items-start" data-id="element-1919">
              <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1920">
                Your Selections
              </span>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1921">
                ${totalPrice.toLocaleString()}
              </span>
            </div>
            <ChevronUp className={`w-4 h-4 ml-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1922" />
          </motion.button>}
      </AnimatePresence>

      {/* Expanded Panel */}
      <AnimatePresence data-id="element-1923">
        {isExpanded && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={finalizeStep === 'review' ? handleClose : undefined} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[95]" data-id="element-1924" />

            {/* Panel - Centered modal instead of bottom sheet */}
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className={`fixed top-8 bottom-8 right-6 w-full max-w-md z-[100] rounded-2xl shadow-2xl backdrop-blur-xl border flex flex-col overflow-hidden ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-1925">
              {/* Review Step */}
              {finalizeStep === 'review' && <>
                  {/* Header */}
                  <div className={`flex items-center justify-between p-5 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1926">
                    <div className="flex items-center gap-3" data-id="element-1927">
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1928">
                        <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1929" />
                      </div>
                      <div data-id="element-1930">
                        <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1931">
                          Your Selections
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1932">
                          {itemCount} upgrade{itemCount !== 1 ? 's' : ''}{' '}
                          selected
                        </p>
                      </div>
                    </div>
                    <button onClick={handleClose} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`} data-id="element-1933">
                      <X className="w-5 h-5" data-id="element-1934" />
                    </button>
                  </div>

                  {/* Selections List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-id="element-1935">
                    {/* Active Package Group */}
                    {activePackage && <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-teal-500/30 bg-teal-50/50'}`} data-id="element-1936">
                        {/* Package Header */}
                        <div className={`flex items-center gap-3 p-4 ${isDark ? 'bg-cyan-500/10' : 'bg-teal-50'}`} data-id="element-1937">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1938">
                            <Package className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1939" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1940">
                            <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-1941">
                              Kitchen Package
                            </p>
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1942">
                              {activePackage.tier} Package
                            </p>
                          </div>
                          {activePackage.price !== undefined && activePackage.price > 0 && <span className={`text-lg font-bold flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1943">
                                ${activePackage.price.toLocaleString()}
                              </span>}
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1944">
                            {onSwap && <button onClick={() => {
                      setIsExpanded(false);
                      onSwap?.('Kitchen', 'Kitchen Packages');
                    }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Package" data-id="element-1945">
                                <ArrowLeftRight className="w-4 h-4" data-id="element-1946" />
                              </button>}
                            <button onClick={clearActivePackage} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Package" data-id="element-1947">
                              <X className="w-4 h-4" data-id="element-1948" />
                            </button>
                          </div>
                        </div>
                        {/* Reset to Package button when overrides exist */}
                        {packageOverrides.length > 0 && <div className={`mx-4 my-3 flex items-center gap-2`} data-id="element-1949">
                            <span className={`text-[10px] font-medium ${isDark ? 'text-amber-400/70' : 'text-amber-600/70'}`} data-id="element-1950">
                              {packageOverrides.length} item
                              {packageOverrides.length !== 1 ? 's' : ''}{' '}
                              modified
                            </span>
                            <button onClick={resetPackageOverrides} className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-colors ${isDark ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20' : 'text-amber-600 bg-amber-50 hover:bg-amber-100'}`} data-id="element-1951">
                              Reset to Package
                            </button>
                          </div>}

                        {/* Package Items (nested) */}
                        <div className="p-2 space-y-1" data-id="element-1952">
                          {selections.filter(s => s.isPackageItem || isItemOverridingPackage(s.itemName)).map(selection => {
                    const isOverride = isItemOverridingPackage(selection.itemName);
                    const originalPrice = getPackageItemPrice(selection.itemName);
                    const delta = isOverride && originalPrice !== undefined ? selection.price - originalPrice : 0;
                    return <div key={selection.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isOverride ? isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200/50' : isDark ? 'bg-white/5' : 'bg-white/80'}`} data-id="element-1953">
                                  {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-1954" /> : <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-1955">
                                      <Lock className={`w-3 h-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1956" />
                                    </div>}
                                  <div className="flex-1 min-w-0" data-id="element-1957">
                                    <div className="flex items-center gap-1.5" data-id="element-1958">
                                      <p className={`text-xs font-medium truncate ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1959">
                                        {selection.itemName}
                                      </p>
                                      {isOverride && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`} data-id="element-1960">
                                          Upgraded
                                        </span>}
                                    </div>
                                    {isOverride && (() => {
                          const original = getOriginalPackageOption(selection.itemName);
                          return original ? <p className={`text-[10px] truncate line-through ${isDark ? 'text-white/25' : 'text-gray-300'}`} data-id="element-1961">
                                            {original.optionName}
                                          </p> : null;
                        })()}
                                    <p className={`text-[10px] truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1962">
                                      {selection.optionName}
                                    </p>
                                    {selection.pricePerSqft !== undefined && selection.areaSqft !== undefined && selection.pricePerSqft > 0 && <p className={`text-[9px] mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-400/70'}`} data-id="element-1963">
                                          ≈ {selection.areaSqft} sq ft × $
                                          {selection.pricePerSqft.toFixed(2)}/sq
                                          ft
                                        </p>}
                                  </div>
                                  {isOverride && delta > 0 && <span className={`text-xs font-bold flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-1964">
                                      +${delta.toLocaleString()}
                                    </span>}
                                </div>;
                  })}
                        </div>
                      </div>}

                    {/* Non-package selections (excluding package overrides) */}
                    {selections.filter(s => !s.isPackageItem && !isItemOverridingPackage(s.itemName)).map((selection, index) => <SelectionCard key={selection.id} selection={selection} index={index} onRemove={() => removeSelection(selection.id)} onSwap={() => handleSwap(selection.category, selection.itemName)} isDark={isDark} data-id="element-1965" />)}

                    {selections.length === 0 && <div className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1966">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-1967" />
                        <p data-id="element-1968">No selections yet</p>
                        <p className="text-sm mt-1" data-id="element-1969">
                          Browse options to add upgrades
                        </p>
                      </div>}
                  </div>

                  {/* Footer */}
                  {selections.length > 0 && <div className={`p-5 border-t flex-shrink-0 ${isDark ? 'border-white/10 bg-slate-900/50' : 'border-gray-200 bg-gray-50/50'}`} data-id="element-1970">
                      <div className="flex items-center justify-between mb-4" data-id="element-1971">
                        <div data-id="element-1972">
                          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1973">
                            Total Upgrade Value
                          </p>
                          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1974">
                            ${totalPrice.toLocaleString()}
                          </p>
                        </div>
                        {confirmClear ? <div className="flex items-center gap-2" data-id="element-1975">
                            <span className={`text-xs font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1976">
                              Clear all?
                            </span>
                            <button onClick={() => {
                    clearSelections();
                    setConfirmClear(false);
                  }} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-50 text-red-500 hover:bg-red-100'}`} data-id="element-1977">
                              Yes, clear
                            </button>
                            <button onClick={() => setConfirmClear(false)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-white/50 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-1978">
                              Cancel
                            </button>
                          </div> : <button onClick={() => setConfirmClear(true)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`} data-id="element-1979">
                            <Trash2 className="w-4 h-4" data-id="element-1980" />
                            Clear All
                          </button>}
                      </div>
                      <button onClick={handleFinalize} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1981">
                        <Send className="w-5 h-5" data-id="element-1982" />
                        Finalize Selections
                      </button>
                      {/* Auth mode toggle for demo */}
                      <div className={`flex items-center justify-center gap-2 mt-3 pt-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1983">
                        <span className={`text-[10px] uppercase tracking-wider font-medium ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1984">
                          Demo mode:
                        </span>
                        <div className={`flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1985">
                          <button onClick={() => setAuthMode('authenticated')} className={`px-2.5 py-1 text-[10px] font-bold transition-colors ${authMode === 'authenticated' ? isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600' : isDark ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1986">
                            Authenticated
                          </button>
                          <button onClick={() => setAuthMode('guest')} className={`px-2.5 py-1 text-[10px] font-bold transition-colors border-l ${authMode === 'guest' ? isDark ? 'bg-amber-500/20 text-amber-400 border-white/10' : 'bg-amber-50 text-amber-600 border-gray-200' : isDark ? 'text-white/30 hover:text-white/50 border-white/10' : 'text-gray-400 hover:text-gray-600 border-gray-200'}`} data-id="element-1987">
                            Guest
                          </button>
                        </div>
                      </div>
                    </div>}
                </>}

              {/* Email Step */}
              {finalizeStep === 'email' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="flex flex-col p-6" data-id="element-1988">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1989">
                    <button onClick={() => setFinalizeStep('review')} className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-1990">
                      <ArrowLeft className="w-5 h-5" data-id="element-1991" />
                    </button>
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1992">
                      Where should we send this?
                    </h2>
                  </div>

                  <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1993">
                    We need your email to send the selection summary and connect
                    your choices to your profile.
                  </p>

                  <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4" data-id="element-1994">
                    <div className={`relative rounded-xl border-2 transition-all ${isDark ? 'border-cyan-400/50 bg-white/[0.12] focus-within:border-cyan-400 focus-within:shadow-[0_0_24px_rgba(6,182,212,0.25)]' : 'border-teal-500/40 bg-white focus-within:border-teal-500 focus-within:shadow-[0_0_24px_rgba(20,184,166,0.2)]'}`} data-id="element-1995">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-cyan-400/70' : 'text-teal-500/60'}`} data-id="element-1996" />
                      <input type="email" required autoFocus placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className={`w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium transition-colors outline-none bg-transparent ${isDark ? 'text-white placeholder-white/50' : 'text-gray-900 placeholder-gray-400'}`} data-id="element-1997" />
                    </div>
                    <button type="submit" disabled={!email.includes('@')} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${!email.includes('@') ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1998">
                      Continue
                    </button>
                  </form>
                </motion.div>}

              {/* OTP Step */}
              {finalizeStep === 'otp' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="flex flex-col p-6" data-id="element-1999">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2000">
                    <button onClick={() => setFinalizeStep('email')} className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-2001">
                      <ArrowLeft className="w-5 h-5" data-id="element-2002" />
                    </button>
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2003">
                      Verify your email
                    </h2>
                  </div>

                  <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-2004">
                    We sent a 6-digit code to{' '}
                    <strong className={isDark ? 'text-white' : 'text-gray-900'} data-id="element-2005">
                      {email}
                    </strong>
                  </p>

                  <form onSubmit={handleOtpSubmit} className="flex flex-col gap-6" data-id="element-2006">
                    <div className="flex justify-between gap-2" data-id="element-2007">
                      {otpDigits.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 text-center text-xl font-bold rounded-lg transition-colors outline-none border ${otpError ? 'border-red-500/50 focus:border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-cyan-500/50 focus:bg-white/10' : 'bg-white border-gray-200 text-gray-900 focus:border-teal-500/50 focus:ring-4 focus:ring-teal-500/10'}`} data-id="element-2008" />)}
                    </div>
                    {otpError && <p className="text-red-400 text-xs text-center -mt-2" data-id="element-2009">
                        Please enter a valid 6-digit code
                      </p>}

                    <div className="space-y-4" data-id="element-2010">
                      <button type="submit" disabled={isVerifying || otpDigits.some(d => !d)} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isVerifying || otpDigits.some(d => !d) ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-teal-600 hover:bg-teal-500'}`} data-id="element-2011">
                        {isVerifying ? <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" data-id="element-2012" /> : <>
                            <Lock className="w-4 h-4" data-id="element-2013" />
                            Verify & Submit
                          </>}
                      </button>
                      <button type="button" className={`w-full text-xs font-medium transition-colors ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-teal-600 hover:text-teal-700'}`} data-id="element-2014">
                        Resend code
                      </button>
                    </div>
                  </form>
                </motion.div>}

              {/* Processing Step */}
              {finalizeStep === 'processing' && <div className="flex-1 flex flex-col items-center justify-center p-8" data-id="element-2015">
                  <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="text-center" data-id="element-2016">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear'
              }} className={`w-16 h-16 mx-auto mb-6 rounded-full border-4 border-t-transparent ${isDark ? 'border-cyan-500' : 'border-teal-500'}`} data-id="element-2017" />
                    <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2018">
                      Processing Your Selections
                    </h2>
                    <div className="space-y-3" data-id="element-2019">
                      <ProcessingStep icon={<FileText className="w-4 h-4" data-id="element-2021" />} text="Generating selection summary..." isDark={isDark} delay={0} data-id="element-2020" />
                      <ProcessingStep icon={<Building2 className="w-4 h-4" data-id="element-2023" />} text="Sending to Landmark Homes..." isDark={isDark} delay={0.8} data-id="element-2022" />
                      <ProcessingStep icon={<Mail className="w-4 h-4" data-id="element-2025" />} text="Preparing your email confirmation..." isDark={isDark} delay={1.6} data-id="element-2024" />
                    </div>
                  </motion.div>
                </div>}

              {/* Success Step */}
              {finalizeStep === 'success' && <div className="flex flex-col max-h-full overflow-hidden" data-id="element-2026">
                  {/* Scrollable content */}
                  <div className="flex-1 overflow-y-auto p-6" data-id="element-2027">
                    <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="text-center" data-id="element-2028">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200,
                  delay: 0.2
                }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`} data-id="element-2029">
                        <CheckCircle2 className={`w-10 h-10 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-2030" />
                      </motion.div>

                      <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2031">
                        Selections Submitted!
                      </h2>
                      <p className={`text-sm mb-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-2032">
                        Your selections have been sent to Landmark Homes
                      </p>

                      {/* Summary Card */}
                      <div className={`rounded-xl p-4 mb-4 text-left ${isDark ? 'bg-slate-800/50 border border-white/10' : 'bg-gray-50 border border-gray-200'}`} data-id="element-2033">
                        <div className="flex items-center justify-between mb-2" data-id="element-2034">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-2035">
                            Total Upgrades
                          </span>
                          <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2036">
                            {itemCount} items
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-2037">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-2038">
                            Total Value
                          </span>
                          <span className={`text-lg font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-2039">
                            ${totalPrice.toLocaleString()}
                          </span>
                        </div>
                        <div className={`pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-2040">
                          <div className="flex items-center gap-2 text-sm" data-id="element-2041">
                            <Mail className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-2042" />
                            <span className={isDark ? 'text-white/70' : 'text-gray-600'} data-id="element-2043">
                              Confirmation sent to{' '}
                              <span className="font-medium" data-id="element-2044">
                                demo@example.com
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* What's Next */}
                      <div className={`rounded-xl p-4 text-left ${isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-teal-50 border border-teal-200'}`} data-id="element-2045">
                        <h3 className={`font-semibold mb-3 ${isDark ? 'text-cyan-400' : 'text-teal-700'}`} data-id="element-2046">
                          What happens next?
                        </h3>
                        <ul className={`text-sm space-y-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-2047">
                          <li className="flex gap-3" data-id="element-2048">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-2049" />
                            <span data-id="element-2050">
                              A design consultant will review your selections
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-2051">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-2052" />
                            <span data-id="element-2053">
                              Receive a detailed quote within 24-48 hours
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-2054">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-2055" />
                            <span data-id="element-2056">
                              Schedule a call to finalize your dream home
                            </span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  </div>

                  {/* Fixed Actions at bottom */}
                  <div className={`p-5 border-t flex-shrink-0 space-y-2 ${isDark ? 'border-white/10 bg-slate-900/80' : 'border-gray-200 bg-white/80'}`} data-id="element-2057">
                    <button onClick={handleClose} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-2058">
                      Continue Exploring
                    </button>
                    <button onClick={handleStartOver} className={`w-full py-2.5 rounded-xl font-medium transition-all ${isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-2059">
                      Start New Selection
                    </button>
                  </div>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}
interface ProcessingStepProps {
  icon: React.ReactNode;
  text: string;
  isDark: boolean;
  delay: number;
}
function ProcessingStep({
  icon,
  text,
  isDark,
  delay
}: ProcessingStepProps) {
  return <motion.div initial={{
    opacity: 0,
    x: -10
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay
  }} className={`flex items-center gap-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-2060">
      <div className={`p-1.5 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-2061">
        {icon}
      </div>
      <span className="text-sm" data-id="element-2062">{text}</span>
    </motion.div>;
}
interface SelectionCardProps {
  selection: Selection;
  index: number;
  onRemove: () => void;
  onSwap?: () => void;
  isDark: boolean;
}
function SelectionCard({
  selection,
  index,
  onRemove,
  onSwap,
  isDark
}: SelectionCardProps) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    delay: index * 0.05
  }} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${isDark ? 'bg-slate-800/50 border-white/5 hover:border-white/10' : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'}`} data-id="element-2063">
      {/* Thumbnail */}
      {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" data-id="element-2064" /> : <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-2065">
          <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2066" />
        </div>}

      {/* Info */}
      <div className="flex-1 min-w-0" data-id="element-2067">
        <p className={`text-xs font-medium uppercase tracking-wider mb-0.5 ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-2068">
          {selection.category}
        </p>
        <p className={`font-semibold truncate text-sm ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2069">
          {selection.itemName}
        </p>
        <p className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-2070">
          {selection.optionName}
        </p>
        {selection.pricePerSqft !== undefined && selection.areaSqft !== undefined && selection.pricePerSqft > 0 && <p className={`text-[10px] mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-2071">
              ≈ {selection.areaSqft} sq ft × $
              {selection.pricePerSqft.toFixed(2)}/sq ft
            </p>}
      </div>

      {/* Price & Actions */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-2072">
        <span className={`text-base font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-2073">
          ${selection.price.toLocaleString()}
        </span>
        <div className="flex items-center gap-1" data-id="element-2074">
          {onSwap && <button onClick={onSwap} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Item" data-id="element-2075">
              <ArrowLeftRight className="w-4 h-4" data-id="element-2076" />
            </button>}
          <button onClick={onRemove} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Item" data-id="element-2077">
            <X className="w-4 h-4" data-id="element-2078" />
          </button>
        </div>
      </div>
    </motion.div>;
}